<template>
  <div v-if="upcoming.length || recurring.length" class="sf-community-events">
    <section v-if="upcoming.length">
      <h2>Upcoming events</h2>
      <article v-for="event in upcoming" :key="event.id || event.slug">
        <img v-if="event.thumbnail || event.cover"
          :src="event.thumbnail || event.cover" alt="" loading="lazy">
        <div>
          <h3>{{ event.title }}</h3>
          <p>{{ event.date || event.eventdate_start || event.eventdate }}</p>
          <p v-if="event.small_description">{{ event.small_description }}</p>
          <a v-if="event.ispage && event.url" :href="event.url">
            View event details →
          </a>
        </div>
      </article>
    </section>

    <section v-if="recurring.length">
      <h2>Regular gatherings</h2>
      <article v-for="event in recurring" :key="event.id || event.slug">
        <div>
          <h3>{{ event.title }}</h3>
          <p v-if="event.schedule">{{ event.schedule }}</p>
          <p v-else>Check with the community for current gathering times.</p>
          <a v-if="event.ispage && event.url" :href="event.url">
            View gathering details →
          </a>
        </div>
      </article>
    </section>
  </div>
</template>

<script>
import moment from 'moment'

const formats = [
  moment.ISO_8601,
  'MMMM D,YYYY',
  'MMMM D, YYYY',
  'MMM D, YYYY'
]

function parseDate(value) {
  if (!value || typeof value !== 'string') return null;
  const date = moment(value.trim(), formats, true);
  return date.isValid() ? date : null;
}

function firstDate(event, fields) {
  for (const field of fields) {
    const date = parseDate(event[field]);
    if (date) return date;
  }
  return null;
}

function repeats(event) {
  return Boolean(
    Array.isArray(event.weekdays)
      ? event.weekdays.length
      : typeof event.weekdays === 'string' && event.weekdays.trim()
  );
}

export default {
  name: 'CommunityEvents',
  props: {
    events: {
      type: Array,
      default: () => []
    }
  },
  computed: {
    upcoming() {
      const today = moment().startOf('day');
      return this.events
        .filter(event => {
          if (repeats(event)) return false;
          const date = firstDate(event, [
            'eventdate_end', 'eventdate_start', 'eventdate', 'date'
          ]);
          return date && date.isSameOrAfter(today, 'day');
        })
        .slice()
        .sort((a, b) => {
          const fields = [
            'eventdate_start', 'eventdate', 'date', 'eventdate_end'
          ];
          return firstDate(a, fields).valueOf() -
            firstDate(b, fields).valueOf();
        });
    },
    recurring() {
      const today = moment().startOf('day');
      return this.events.filter(event => {
        if (!repeats(event)) return false;
        const end = parseDate(event.eventdate_end);
        return !end || end.isSameOrAfter(today, 'day');
      });
    }
  }
}
</script>

<style scoped>
section + section {
  margin-top: 30px;
}
h2 {
  margin: 0 0 20px;
  font-family: Georgia, serif;
  font-size: 28px;
  font-weight: 400;
  color: #26362e;
}
article {
  display: flex;
  gap: 18px;
  padding: 18px;
  margin-bottom: 14px;
  border: 1px solid #e5e3d8;
  border-radius: 12px;
  background: #fff;
}
article img {
  width: 110px;
  height: 100px;
  border-radius: 8px;
  object-fit: cover;
}
article > div {
  min-width: 0;
}
h3 {
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 700;
}
p, a {
  font-size: 13px;
  line-height: 1.7;
  overflow-wrap: anywhere;
}
p {
  margin: 0 0 8px;
  color: #62695f;
}
a {
  color: #26362e;
  font-weight: 700;
}
@media (max-width: 500px) {
  article {
    flex-direction: column;
  }
  article img {
    width: 100%;
    height: 160px;
  }
}
</style>
